import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FadeIn } from "@/components/ui/scroll-animations";
import { submitLead } from "@/lib/submit-lead";
import type { LeadSource } from "@/lib/lead-sources";
import type { ReactNode } from "react";

const investorProfiles = [
  { value: "individual", label: "Individual" },
  { value: "institutional_allocator", label: "Institutional allocator" },
  { value: "family_office", label: "Family office" },
  { value: "strategic_capital", label: "Strategic capital" },
  { value: "other", label: "Other qualified investor" },
] as const;

const contactSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(7, "Valid mobile number is required"),
  message: z.string().min(10, "Please provide more details (min 10 chars)"),
});

const velocitySchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(7, "Valid mobile number is required"),
  investorType: z.string().min(1, "Investor profile is required"),
});

type ContactFormData = z.infer<typeof contactSchema>;
type VelocityFormData = z.infer<typeof velocitySchema>;

type ContactInquiryFormProps =
  | { source: LeadSource; variant?: "contact" }
  | { source: "velocity-fund"; variant: "velocity" };

function formatInvestorProfile(value: string): string {
  return investorProfiles.find((profile) => profile.value === value)?.label ?? value;
}

export function ContactInquiryForm(props: ContactInquiryFormProps) {
  const { source } = props;
  const variant = props.variant ?? "contact";
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const contactForm = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", phone: "", message: "" },
  });

  const velocityForm = useForm<VelocityFormData>({
    resolver: zodResolver(velocitySchema),
    defaultValues: { name: "", email: "", phone: "", investorType: "" },
  });

  const onSubmitContact = async (data: ContactFormData) => {
    setIsLoading(true);
    contactForm.clearErrors("root");
    try {
      await submitLead({ ...data, source });
      setIsSubmitted(true);
      contactForm.reset();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Something went wrong. Please email us directly.";
      contactForm.setError("root", { message });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitVelocity = async (data: VelocityFormData) => {
    setIsLoading(true);
    velocityForm.clearErrors("root");
    try {
      await submitLead({
        name: data.name,
        email: data.email,
        phone: data.phone,
        investorType: data.investorType,
        company: "Velocity Fund Inquiry",
        source,
        message: `Velocity Fund access request — investor profile: ${formatInvestorProfile(data.investorType)}.`,
      });
      setIsSubmitted(true);
      velocityForm.reset();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Something went wrong. Please email us directly.";
      velocityForm.setError("root", { message });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <InquirySuccessState
        title={variant === "velocity" ? "Inquiry received" : "Thank you"}
        description={
          variant === "velocity"
            ? "Our team has your mandate profile and will review it before reaching out."
            : "We've received your inquiry and will respond shortly."
        }
        onReset={() => setIsSubmitted(false)}
        resetLabel={variant === "velocity" ? "Submit another inquiry" : "Send another message"}
      />
    );
  }

  if (variant === "velocity") {
    return (
      <form onSubmit={velocityForm.handleSubmit(onSubmitVelocity)} className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Name" error={velocityForm.formState.errors.name?.message}>
            <Input {...velocityForm.register("name")} className="gc-input" placeholder="Your name" />
          </Field>
          <Field label="Email" error={velocityForm.formState.errors.email?.message}>
            <Input
              {...velocityForm.register("email")}
              type="email"
              className="gc-input"
              placeholder="you@company.com"
            />
          </Field>
        </div>
        <Field label="Mobile" error={velocityForm.formState.errors.phone?.message}>
          <Input {...velocityForm.register("phone")} className="gc-input" placeholder="+91" />
        </Field>
        <Field label="Investor profile" error={velocityForm.formState.errors.investorType?.message}>
          <Select onValueChange={(value) => velocityForm.setValue("investorType", value)}>
            <SelectTrigger
              className={`gc-input ${velocityForm.formState.errors.investorType ? "border-destructive" : ""}`}
            >
              <SelectValue placeholder="Select profile" />
            </SelectTrigger>
            <SelectContent>
              {investorProfiles.map((profile) => (
                <SelectItem key={profile.value} value={profile.value}>
                  {profile.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {velocityForm.formState.errors.root && (
          <p className="text-sm text-destructive">{velocityForm.formState.errors.root.message}</p>
        )}
        <Button disabled={isLoading} className="gc-btn-primary w-full">
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              Submit inquiry
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={contactForm.handleSubmit(onSubmitContact)} className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Name" error={contactForm.formState.errors.name?.message}>
          <Input {...contactForm.register("name")} className="gc-input" placeholder="Your name" />
        </Field>
        <Field label="Email" error={contactForm.formState.errors.email?.message}>
          <Input
            {...contactForm.register("email")}
            type="email"
            className="gc-input"
            placeholder="you@company.com"
          />
        </Field>
      </div>
      <Field label="Mobile" error={contactForm.formState.errors.phone?.message}>
        <Input {...contactForm.register("phone")} className="gc-input" placeholder="+91" />
      </Field>
      <Field label="Message" error={contactForm.formState.errors.message?.message}>
        <Textarea
          {...contactForm.register("message")}
          className="gc-input min-h-[140px]"
          placeholder="Tell us about your mandate..."
        />
      </Field>
      {contactForm.formState.errors.root && (
        <p className="text-sm text-destructive">{contactForm.formState.errors.root.message}</p>
      )}
      <Button
        type="submit"
        disabled={isLoading}
        data-cursor-label="Send"
        className="rounded-none px-10 h-12 bg-foreground text-background hover:bg-foreground/90 font-normal tracking-wide w-full sm:w-auto"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          "Submit inquiry"
        )}
      </Button>
    </form>
  );
}

function InquirySuccessState({
  title,
  description,
  onReset,
  resetLabel,
}: {
  title: string;
  description: string;
  onReset: () => void;
  resetLabel: string;
}) {
  return (
    <FadeIn>
      <div className="border border-border bg-background p-10 md:p-12 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-border">
          <Check className="h-6 w-6 text-foreground" />
        </div>
        <h3 className="mt-6 text-2xl font-medium tracking-tight">{title}</h3>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
        <Button variant="outline" className="rounded-none mt-8" onClick={onReset}>
          {resetLabel}
        </Button>
      </div>
    </FadeIn>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label className="gc-field-label">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
